import { DeliveryEntity } from "../../../Objects/DeliveryEntity/DeliveryEntity"
import { EDeliveryStatusType } from "../../../Enum/EDeliveryStatusType"
import { useTranslation } from "react-i18next"
import { useConfirmation } from "../../../context/useConfirmationContext"

interface Props {
  delivery: DeliveryEntity
  onCancelSearch: (deliveryId: number) => void
}

export const RenderDeliveryDetails: React.FC<Props> = ({ delivery, onCancelSearch }) => {
  const { t } = useTranslation("frontend")

  const { setNewConfirmation } = useConfirmation()

  const isLoading = delivery.IsFindingCourier()

  const statusConfig = {
    [EDeliveryStatusType.Pending]: {
      label: "Buscando entregador",
      bg: "bg-white",
      badge: "bg-gray-200 text-gray-700",
    },
    [EDeliveryStatusType.Rejected]: {
      label: "Entregador rejeitou",
      bg: "bg-white",
      badge: "bg-red-200 text-red-800",
    },
    [EDeliveryStatusType.Canceled]: {
      label: "Entregador cancelou",
      bg: "bg-white",
      badge: "bg-red-200 text-red-800",
    },
    [EDeliveryStatusType.Expired]: {
      label: "Entregador expirou",
      bg: "bg-white",
      badge: "bg-red-200 text-red-800",
    },
    [EDeliveryStatusType.Offered]: {
      label: "Oferecido ao entregador",
      bg: "bg-white",
      badge: "bg-yellow-200 text-yellow-800",
    },
    [EDeliveryStatusType.Accepted]: {
      label: "Entregador aceitou",
      bg: "bg-white",
      badge: "bg-blue-200 text-blue-800",
    },
    [EDeliveryStatusType.PickedUp]: {
      label: "Pedido coletado",
      bg: "bg-white",
      badge: "bg-purple-200 text-purple-800",
    },
    [EDeliveryStatusType.OnRoute]: {
      label: "A caminho",
      bg: "bg-white",
      badge: "bg-orange-200 text-orange-800",
    },
    [EDeliveryStatusType.Delivered]: {
      label: "Entregue",
      bg: "bg-white",
      badge: "bg-green-200 text-green-800",
    },
  }

  const config = statusConfig[delivery.status]
  if (!config) return null

  return (
    <div
      className={`
        relative overflow-hidden
        rounded-2xl p-5
        ring-2 ring-slate-200
        bg-white
        transition-all duration-300
        ${config.bg}
      `}
    >
      <div className="flex justify-between items-center mb-4">
        <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Entrega</span>

        <span
          className={`
            text-xs font-bold px-3 py-1 rounded-full
            ${config.badge}
          `}
        >
          {config.label}
        </span>
      </div>

      {isLoading && (
        <>
          <div className="flex items-center gap-4 mb-4 mt-2 border-t pt-4 border-slate-200">
            <div className="w-5 h-5 shrink-0 box-border border-2 border-gray-300 border-t-orange-500 rounded-full animate-spin" />
            <span className="text-sm text-gray-600 font-medium">{t("kitchen.searching_couriers")}</span>
          </div>

          <div className="flex flex-col items-start gap-2 border-t pt-2 border-slate-200">
            <span className="text-[12px] text-gray-600 font-medium mt-2">
              <span className="font-semibold">Iniciado: </span>
              {delivery.createdDatetime.toString().toReadableDate()}
            </span>

            <span className="text-[12px] text-gray-600 font-medium">
              <span className="font-semibold">Atualizado: </span>
              {delivery.updatedDatetime.toString().toReadableDate()}
            </span>

            <button
              onClick={() =>
                setNewConfirmation("Cancelar busca", "Tem certeza que deseja cancelar a busca por entregadores?", () =>
                  onCancelSearch(delivery.id),
                )
              }
              className="mt-2 w-full rounded-lg border border-red-200 bg-red-50 py-2 text-xs font-bold text-red-600 transition hover:bg-red-100"
              data-interaction-description-key="interactionDescriptions:pages.kitchenPage.components.renderDeliveryDetails.button.setNewConfirmationCancelarBuscaTemCertezaQueDesejaCancelarAB"
            >
              Cancelar busca
            </button>
          </div>
        </>
      )}

      {!isLoading && (
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Entregador</span>
            <span className="font-semibold text-slate-800">
              {delivery.courierEntity?.personEntity?.name ?? "Aguardando"}
            </span>
          </div>

          {delivery.estimatedDistanceKm !== undefined && (
            <div className="flex justify-between">
              <span className="text-slate-500">{t("kitchen.distance")}</span>
              <span className="font-semibold text-slate-800">{delivery.estimatedDistanceKm.toFixed(2)} km</span>
            </div>
          )}
        </div>
      )}

      {delivery.status === EDeliveryStatusType.Accepted && (
        <button
          onClick={() =>
            setNewConfirmation("Cancelar entrega", "Tem certeza que deseja cancelar esta entrega?", () =>
              onCancelSearch(delivery.id),
            )
          }
          className="mt-2 w-full rounded-lg border border-red-200 bg-red-50 py-2 text-xs font-bold text-red-600 transition hover:bg-red-100"
          data-interaction-description-key="interactionDescriptions:pages.kitchenPage.components.renderDeliveryDetails.button.setNewConfirmationCancelarEntregaTemCertezaQueDesejaCancelar"
        >
          Cancelar entrega
        </button>
      )}

      {delivery.status === EDeliveryStatusType.OnRoute && (
        <div className="mt-4 h-2 w-full bg-orange-200 rounded-full overflow-hidden">
          <div className="h-full bg-orange-500 animate-pulse w-2/3" />
        </div>
      )}
    </div>
  )
}
